import type { DataProvider, DeleteResult } from 'react-admin';

const API_URL = window.__ROSTA_API_URL__
  || import.meta.env.VITE_API_URL
  || 'http://localhost:3200/api/v1';

let authTokenGetter: (() => string | null) | null = null;
let onUnauthorized: (() => void) | null = null;
let tenantIdGetter: (() => string | null) | null = null;

export const setAuthTokenGetter = (getter: (() => string | null) | null) => {
  authTokenGetter = getter;
};

export const setOnUnauthorized = (handler: (() => void) | null) => {
  onUnauthorized = handler;
};

export const setTenantIdGetter = (getter: (() => string | null) | null) => {
  tenantIdGetter = getter;
};

const RESOURCE_MAP: Record<string, string> = {
  tutors: 'tutoring/tutors',
  'tutors/employee': 'tutoring/tutors/employee',
  clients: 'tutoring/clients',
  students: 'tutoring/students',
  qualifications: 'tutoring/qualifications',
  'qualifications/bulk': 'tutoring/qualifications/bulk',
  'qualification-types': 'tutoring/qualification-types',
  services: 'tutoring/services',
  'service-authorisations': 'tutoring/service-authorisations',
  'tutoring-agreements': 'tutoring/tutoring-agreements',
  'tutoring-schedules': 'tutoring/tutoring-schedules',
  'tutoring-sessions': 'tutoring/tutoring-sessions',
  attendance: 'tutoring/attendance',
  entitlements: 'domains/tenants',
  // React Admin resource name (plural) → API route (singular)
  'identity-verifications': 'identity-verification',
  // RA name plural, controller path singular (M08 T1.3)
  'service-catalogues': 'service-catalogue',
  // Controller path already plural — identity map, intent documented (M08 T2.4)
  'service-orders': 'service-orders',
};

function apiResource(resource: string): string {
  return RESOURCE_MAP[resource] ?? resource;
}

const fetchJson = async (url: string, options: RequestInit = {}) => {
  const token = authTokenGetter?.();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (response.status === 401 && onUnauthorized) {
    onUnauthorized();
    throw new Error('Session expired');
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const message = body.detail
      || (typeof body.message === 'string' ? body.message : Array.isArray(body.message) ? body.message.join('; ') : null)
      || response.statusText
      || `HTTP ${response.status}`;
    throw new Error(message);
  }

  if (response.status === 204) {
    return { json: null };
  }

  return { json: await response.json() };
};

const withId = <T extends Record<string, unknown>>(record: T): T & { id: string } => ({
  ...record,
  id: String(record._id ?? record.id),
});

export const apiDataProvider: DataProvider = {
  getList: async (resource, params) => {
    const { page, perPage } = params.pagination ?? { page: 1, perPage: 25 };
    const { field, order } = params.sort ?? { field: '_id', order: 'ASC' };

    const queryParams = new URLSearchParams({
      page: String(page),
      perPage: String(perPage),
      sort: field,
      order: order,
    });

    const tenantId = tenantIdGetter?.();
    if (tenantId) {
      queryParams.set('tenantId', tenantId);
    }

    // Pass filter params to API for server-side filtering
    if (params.filter) {
      for (const [key, value] of Object.entries(params.filter)) {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.set(key, String(value));
        }
      }
    }

    const { json } = await fetchJson(`${API_URL}/${apiResource(resource)}?${queryParams}`);

    const items = (json.data ?? json);
    return {
      data: items.map((record: Record<string, unknown>) => {
        const doc = (record.doc ?? record) as Record<string, unknown>;
        return { ...doc, id: String(doc._id ?? doc.id ?? record.id) };
      }),
      total: json.total ?? items.length,
    };
  },

  getOne: async (resource, params) => {
    const { json } = await fetchJson(`${API_URL}/${apiResource(resource)}/${params.id}`);
    const record = json.data ?? json;
    return { data: withId({ ...record, id: record._id ?? record.id }) };
  },

  getMany: async (resource, params) => {
    const promises = params.ids.map(id =>
      fetchJson(`${API_URL}/${apiResource(resource)}/${id}`)
        .then(({ json }) => {
          const record = json.data ?? json;
          return withId({ ...record, id: record._id ?? record.id });
        })
        .catch(() => null)
    );
    const results = await Promise.all(promises);
    return { data: results.filter(Boolean) };
  },

  getManyReference: async (resource, params) => {
    const { page, perPage } = params.pagination ?? { page: 1, perPage: 25 };
    const { field, order } = params.sort ?? { field: '_id', order: 'ASC' };

    const queryParams = new URLSearchParams({
      page: String(page),
      perPage: String(perPage),
      sort: field,
      order: order,
      target: params.target,
      id: String(params.id),
    });

    const { json } = await fetchJson(`${API_URL}/${apiResource(resource)}?${queryParams}`);

    const items = (json.data ?? json);
    return {
      data: items.map((record: Record<string, unknown>) => {
        const doc = (record.doc ?? record) as Record<string, unknown>;
        return { ...doc, id: String(doc._id ?? doc.id ?? record.id) };
      }),
      total: json.total ?? items.length,
    };
  },

  create: async (resource, params) => {
    const { json } = await fetchJson(`${API_URL}/${apiResource(resource)}`, {
      method: 'POST',
      body: JSON.stringify(params.data),
    });
    const record = json.data ?? json;
    return { data: withId({ ...record, id: record._id ?? record.id }) };
  },

  update: async (resource, params) => {
    const { json } = await fetchJson(`${API_URL}/${apiResource(resource)}/${params.id}`, {
      method: 'PATCH',
      body: JSON.stringify(params.data),
    });
    return { data: withId(json.data ?? json) };
  },

  updateMany: async (resource, params) => {
    const promises = params.ids.map(id =>
      fetchJson(`${API_URL}/${apiResource(resource)}/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(params.data),
      }).then(() => id)
    );
    const results = await Promise.all(promises);
    return { data: results };
  },

  delete: async (resource, params) => {
    await fetchJson(`${API_URL}/${apiResource(resource)}/${params.id}`, {
      method: 'DELETE',
    });
    return { data: { id: params.id, _id: params.id } } as DeleteResult;
  },

  deleteMany: async (resource, params) => {
    const promises = params.ids.map(id =>
      fetchJson(`${API_URL}/${apiResource(resource)}/${id}`, {
        method: 'DELETE',
      }).then(() => id)
    );
    const results = await Promise.all(promises);
    return { data: results };
  },

  custom: async (verb: string, resource: string, params?: { body?: unknown; queryParams?: Record<string, string> }) => {
    const url = params?.queryParams
      ? `${API_URL}/${apiResource(resource)}?${new URLSearchParams(params.queryParams)}`
      : `${API_URL}/${apiResource(resource)}`;

    const { json } = await fetchJson(url, {
      method: verb,
      body: params?.body ? JSON.stringify(params.body) : undefined,
    });
    return { data: json.data ?? json, meta: {} };
  },
};
